// src/pages/PrivacyPolicy.js
import React from "react";
import Seo from "../components/Seo";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <Seo
        title="Privacy Policy | Clean Sweeps"
        description="Clean Sweeps privacy policy covering data collection, usage, and cookies."
        path="/policy"
        image="/og/privacy.png"
        imageAlt="Clean Sweeps Privacy Policy"
      />
      <h1>Privacy Policy</h1>
      <p>
        <strong>Effective date:</strong> October 01, 2024
      </p>
      <p>
        <strong>Last updated:</strong> January 05, 2026
      </p>
      <p>
        Clean Sweeps LLC ("us", "we", or "our") operates the Clean Sweeps app
        and related services (the "Service"). This Privacy Policy describes how
        we collect, use, disclose, and otherwise process information, including
        personal data.
      </p>
      <p>
        By using the Service, you agree to the collection and use of information
        in accordance with this policy. This Privacy Policy is incorporated into
        and forms part of our Terms of Service.
      </p>

      <section>
        <h2>Definitions</h2>

        <h3>Service</h3>
        <p>Service is the Clean Sweeps app and related services operated by Clean Sweeps LLC.</p>

        <h3>Personal Data</h3>
        <p>
          Personal Data means information about a living individual who can be
          identified from those data (or from those and other information in our
          possession or likely to come into our possession).
        </p>

        <h3>Usage Data</h3>
        <p>
          Usage Data is data collected automatically either generated by the use
          of the Service or from the Service infrastructure itself (for example,
          the duration of a page visit).
        </p>

        <h3>Cookies</h3>
        <p>Cookies are small files stored on your device (computer or mobile device).</p>
      </section>

      <section>
        <h2>Information Collection and Use</h2>
        <p>
          We collect several different types of information for various purposes
          to provide and improve our Service, to communicate with you, and for
          our business and commercial purposes.
        </p>
      </section>

      <section>
        <h2>Types of Data Collected</h2>

        <h3>Personal Data</h3>
        <p>
          While using our Service, we may collect or receive certain personally
          identifiable information. Personally identifiable information may
          include, but is not limited to:
        </p>
        <ul>
          <li>Email address</li>
          <li>First name and last name</li>
          <li>Phone number</li>
          <li>Address, State, Province, ZIP/Postal code, City</li>
          <li>
            Order information and transaction details (including order IDs,
            purchase history, and related metadata)
          </li>
          <li>Customer lists or participant lists (where made available to us through the Service)</li>
          <li>Cookies and Usage Data</li>
        </ul>

        <h3>Usage Data</h3>
        <p>
          We may also collect information on how the Service is accessed and
          used ("Usage Data"). This Usage Data may include information such as
          your computer's Internet Protocol address (e.g. IP address), browser
          type, browser version, the pages of our Service that you visit, the
          time and date of your visit, the time spent on those pages, unique
          device identifiers and other diagnostic data.
        </p>

        <h3>Tracking &amp; Cookies Data</h3>
        <p>
          We use cookies and similar tracking technologies to track the activity
          on our Service and hold certain information. You can instruct your
          browser to refuse all cookies or to indicate when a cookie is being
          sent. However, if you do not accept cookies, you may not be able to
          use some portions of our Service.
        </p>

        <p>
          <strong>Examples of Cookies we use:</strong>
        </p>
        <ul>
          <li>
            <strong>Session Cookies.</strong> We use Session Cookies to operate
            our Service.
          </li>
          <li>
            <strong>Preference Cookies.</strong> We use Preference Cookies to
            remember your preferences and various settings.
          </li>
          <li>
            <strong>Security Cookies.</strong> We use Security Cookies for
            security purposes.
          </li>
        </ul>
      </section>

      <section>
        <h2>Use of Data</h2>
        <p>Clean Sweeps LLC uses the collected data for various purposes, including:</p>
        <ul>
          <li>To provide and maintain our Service</li>
          <li>To notify you about changes to our Service</li>
          <li>
            To allow you to participate in interactive features of our Service
            when you choose to do so
          </li>
          <li>To provide customer support</li>
          <li>
            To gather analysis or valuable information so that we can improve
            our Service
          </li>
          <li>To monitor the usage of our Service</li>
          <li>To detect, prevent and address technical issues</li>
          <li>
            <strong>Marketing and Promotions.</strong> To send marketing
            communications, promotional materials, and other information that
            may be of interest to you (where permitted by law). You may opt out
            of marketing emails by using the unsubscribe link in the email or by
            contacting us.
          </li>
          <li>
            <strong>Business and Commercial Purposes.</strong> To operate our
            business, develop new products and services, measure campaign
            effectiveness, and for other business purposes.
          </li>
        </ul>

        <p>
          <strong>Sharing and Sale of Personal Data.</strong> To the maximum
          extent permitted by applicable law, we may disclose, share, license,
          or sell Personal Data to third parties for business and commercial
          purposes (including marketing and advertising). Where required by law,
          we will provide an opportunity to opt out of such sale/sharing, or
          obtain consent.
        </p>
      </section>

      <section>
        <h2>Legal Basis for Processing (GDPR / EEA)</h2>
        <p>
          If you are from the European Economic Area (EEA), the legal basis for
          collecting and using the personal information described in this
          Privacy Policy depends on the Personal Data we collect and the
          specific context in which we collect it.
        </p>
        <p>We may process Personal Data under one or more of the following bases:</p>
        <ul>
          <li>We need to perform a contract with you</li>
          <li>You have given us permission to do so (consent)</li>
          <li>The processing is in our legitimate interests and not overridden by your rights</li>
          <li>To comply with the law</li>
        </ul>
        <p>
          Note: If we rely on your consent, you may withdraw consent at any time
          by contacting us at{" "}
          <a href="mailto:info@cleansweeps.app">info@cleansweeps.app</a>.
        </p>
      </section>

      <section>
        <h2>Retention of Data</h2>
        <p>
          Clean Sweeps LLC will retain your Personal Data only for as long as is
          necessary for the purposes set out in this Privacy Policy. We will
          retain and use your Personal Data to the extent necessary to comply
          with our legal obligations, resolve disputes and enforce our legal
          agreements and policies.
        </p>
      </section>

      <section>
        <h2>Transfer of Data</h2>
        <p>
          Your information, including Personal Data, may be transferred to -- and
          maintained on -- computers located outside of your state, province,
          country or other governmental jurisdiction where the data protection
          laws may differ from those of your jurisdiction.
        </p>
        <p>
          Your consent to this Privacy Policy followed by your submission of
          such information represents your agreement to that transfer, subject
          to applicable law.
        </p>
      </section>

      <section>
        <h2>Disclosure of Data</h2>

        <h3>Service Providers</h3>
        <p>
          We may employ third party companies and individuals to facilitate our
          Service ("Service Providers"), provide the Service on our behalf,
          perform Service-related services or assist us in analysing how our
          Service is used. These third parties have access to your Personal Data
          only to perform these tasks on our behalf and are obligated not to
          disclose or use it for any other purpose.
        </p>

        <h3>Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, financing, reorganization,
          bankruptcy, dissolution, or sale/transfer of some or all of our assets,
          your information may be transferred as part of that transaction, subject
          to applicable law.
        </p>

        <h3>Legal Requirements</h3>
        <p>
          Clean Sweeps LLC may disclose your Personal Data in the good faith
          belief that such action is necessary to:
        </p>
        <ul>
          <li>To comply with a legal obligation</li>
          <li>To protect and defend the rights or property of Clean Sweeps LLC</li>
          <li>To prevent or investigate possible wrongdoing in connection with the Service</li>
          <li>To protect the personal safety of users of the Service or the public</li>
          <li>To protect against legal liability</li>
        </ul>
      </section>

      <section>
        <h2>Your Data Protection Rights</h2>

        <h3>EEA / GDPR Rights</h3>
        <p>
          If you are a resident of the European Economic Area (EEA), you have
          certain data protection rights. These include the right to access,
          update, delete, restrict processing, object to processing, and data
          portability. You also have the right to withdraw consent where we rely
          on consent.
        </p>

        <h3>U.S. State Privacy Rights (including California)</h3>
        <p>
          Depending on your state of residence, you may have rights to access,
          delete, correct, or opt out of certain processing of your Personal
          Data, including the sale or sharing of Personal Data for targeted
          advertising. To submit a request, contact us at{" "}
          <a href="mailto:info@cleansweeps.app">info@cleansweeps.app</a>.
        </p>
        <p>
          <strong>Do Not Sell / Share:</strong> If you wish to opt out of the
          sale or sharing of your Personal Data where applicable, email{" "}
          <a href="mailto:info@cleansweeps.app">info@cleansweeps.app</a> with the
          subject line{" "}
          <strong>"Do Not Sell or Share My Personal Information"</strong>.
        </p>
      </section>

      <section>
        <h2>Security of Data</h2>
        <p>
          The security of your data is important to us but remember that no
          method of transmission over the Internet or method of electronic
          storage is 100% secure. While we strive to use commercially acceptable
          means to protect your Personal Data, we cannot guarantee its absolute
          security.
        </p>
      </section>

      <section>
        <h2>Children's Privacy</h2>
        <p>
          Our Service does not address anyone under the age of 18 ("Children").
          We do not knowingly collect personally identifiable information from
          anyone under the age of 18. If we become aware that we have collected
          Personal Data from children without verification of parental consent,
          we take steps to remove that information from our servers.
        </p>
      </section>

      <section>
        <h2>Changes to This Privacy Policy</h2>
        <p>
          We may update our Privacy Policy from time to time. We will notify you
          of any changes by posting the new Privacy Policy on this page and
          updating the effective date. You are advised to review this Privacy
          Policy periodically for any changes. Changes to this Privacy Policy
          are effective when they are posted on this page.
        </p>
      </section>

      <section>
        <h2>Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us:</p>
        <p>
          <strong>Email:</strong>{" "}
          <a href="mailto:info@cleansweeps.app">info@cleansweeps.app</a>
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
